import type { ReactElement } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import cc from 'classcat'

import { switchLanguage } from '../../store/actions'
import { useTranslate } from '../../utils/translate'
import Button from '../formComponents/Button'
import ButtonRow from '../formComponents/ButtonRow'
import SettingsLayer from '../templateComponents/Workspace/SettingsLayer'
import storage from '../../utils/localStorage'

export const lsKey = 'content-editing-locale'

type Props = {
  active: boolean | string
  onToggle: ({ submit }?: { submit?: boolean }) => void
}

export default function LanguageSwitch({ active, onToggle }: Readonly<Props>): ReactElement {
  const dispatch = useDispatch<GlobalDispatch>()

  const t = useTranslate('interface', 'components.editorLanguageSwitchComponent')

  const isBusy = useSelector<State, boolean>((state) => state.getIn(['view', 'busy']))
  const initialShopLocale = useSelector<State, string>((state) => state.getIn(['shop', 'locale']))
  const availableLocales = useSelector<State, ImmutableList>((state) => state.getIn(['shop', 'locales']))
  const virtualRef = useRef<HTMLElement>(null)

  const [shopLocale, setShopLocale] = useState(initialShopLocale)
  const location = useLocation()

  const handleSwitchLanguage = () => {
    storage.setItem(lsKey, shopLocale)

    if (initialShopLocale !== shopLocale) {
      dispatch(switchLanguage(shopLocale, location))
    } else {
      onToggle()
    }
  }

  useEffect(() => {
    if (!storage.getItem(lsKey)) onToggle()
    // We only want this to trigger onMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeClass = active
    ? 'ep-bottom-bar-button-language-switch-arrow-up'
    : 'ep-bottom-bar-button-language-switch-arrow-down'
  const iconClassname = cc([
    'ep-bottom-bar-button',
    'ep-bottom-bar-button-language-switch',
    {
      checked: active,
    },
  ])

  function renderBody() {
    const options = availableLocales.toJS().map((locale: Core.Locale) => {
      const identifier = locale.identifier

      return (
        <option key={identifier} value={identifier}>
          {t(`:enumerations.languages.${identifier.substring(0, 2)}`)}
        </option>
      )
    })

    return (
      <div className="ep-settings-layer-language">
        <div className="ep-form-row">{t('switchLanguageModal.explanation.old')}</div>
        <div className="ep-form-row">
          <label className="ep-form-row-label">
            <label htmlFor="switch-language-modal" className="ep-form-row-label-text">
              {t('switchLanguageModal.languageDropdown.label')}
            </label>
            <select
              className="ep-form-select"
              defaultValue={shopLocale}
              onChange={({ target }) => setShopLocale(target.value)}
              id="switch-language-modal"
            >
              {options}
            </select>
          </label>
        </div>
      </div>
    )
  }
  function renderFooter() {
    return (
      <ButtonRow>
        <Button type="button" variant="cancel" disabled={isBusy} onClick={onToggle}>
          {t('switchLanguageModal.cancelButton.label')}
        </Button>
        <Button disabled={isBusy} type="button" onClick={() => handleSwitchLanguage()}>
          {t('switchLanguageModal.applyButton.label')}
        </Button>
      </ButtonRow>
    )
  }

  return (
    <>
      <span
        ref={virtualRef}
        className="ep-bottom-bar-language-switch"
        title={t('switchLanguageModal.title')}
        onClick={() => onToggle()}
      >
        <span className={iconClassname} />
        <span className="ep-bottom-bar-language-switch-text" ref={virtualRef}>
          {t(`:enumerations.languages.${shopLocale.substring(0, 2)}`)}
        </span>
        <span className={cc(['ep-bottom-bar-language-switch-arrow', activeClass])} />
      </span>
      {active && (
        <SettingsLayer
          open={!!active}
          side="top"
          virtualRef={virtualRef}
          className="ep-settingslayer-language-switch"
          onEscapeKeyDown={() => onToggle()}
          hideArrow={false}
          draggable={false}
          align="center"
          sideOffset={30}
        >
          {({ renderLayout }) => (
            <>
              {renderLayout({
                title: t('switchLanguageModal.title'),
                body: renderBody(),
                footer: renderFooter(),
              })}
            </>
          )}
        </SettingsLayer>
      )}
    </>
  )
}
